import {
  backupHealth,
  clearBackupDismissal,
  dismissBackupPrompt,
  readBackupRecord,
  readDismissal,
  recordBackup,
  type BackupHealth,
  type Timestamp,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'

import { useStorageStatus } from '../pwa/useStorageStatus.js'
import { useServices } from '../services/ServicesProvider.js'

/**
 * How overdue a backup is (T-503).
 *
 * The escalation itself lives in core, because *when to become insistent* is a
 * judgement about risk rather than about layout — and the native app has to
 * inherit it rather than pick its own thresholds. This gathers the inputs and
 * exposes the two things a screen does about it.
 */
export function useBackupHealth(): {
  readonly health: BackupHealth | null
  readonly refresh: () => Promise<void>
  readonly dismiss: () => Promise<void>
  readonly markBackedUp: (at: Timestamp, entryCount: number) => Promise<void>
} {
  const { storage, clock } = useServices()
  const { status } = useStorageStatus()
  const [health, setHealth] = useState<BackupHealth | null>(null)

  const refresh = useCallback(async () => {
    // Nothing is decided until the durability state is known: `evictable`
    // halves every threshold, and guessing it would either nag twice as early
    // as intended or half as often as needed.
    if (status === null) {
      return
    }
    const [record, dismissal, entryCount, first] = await Promise.all([
      readBackupRecord(storage),
      readDismissal(storage),
      storage.entries.count(),
      storage.entries.earliest(),
    ])

    setHealth(
      backupHealth({
        now: clock.now(),
        entryCount,
        evictable: status.state !== 'persistent',
        ...(record === undefined ? {} : { last: record }),
        ...(dismissal === undefined ? {} : { dismissal }),
        ...(first === undefined ? {} : { firstEntryAt: first.at }),
      }),
    )
  }, [storage, clock, status])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const dismiss = useCallback(async () => {
    if (health === null) {
      return
    }
    // The level is recorded, not just the fact of dismissing. A prompt that
    // returns when things get worse is the reason there are levels at all.
    await dismissBackupPrompt(storage, { at: clock.now(), urgency: health.urgency })
    await refresh()
  }, [storage, clock, health, refresh])

  const markBackedUp = useCallback(
    async (at: Timestamp, entryCount: number) => {
      await recordBackup(storage, { at, entryCount })
      await clearBackupDismissal(storage)
      await refresh()
    },
    [storage, refresh],
  )

  return { health, refresh, dismiss, markBackedUp }
}
